import React, { useMemo } from 'react'
import { Box, styled } from '@mui/material'
import { Trans, useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import tokens from '../../../styles/tokens.json'
import { Styles } from '../../../types'
import {
  AutoPaySetup,
  getIsEditAutoPay,
  setAutoPaySetup,
} from '../../../store/invoices/invoiceSelectedInvoicesSlice'
import { useDeviceType } from '../../../rsmCoreComponents/hooks/useDeviceType'
import InvoicesHeader from '../Invoices/InvoicesHeader'
import { getPaymentMethodDisplay } from './PaymentSuccess.service'
import { formatCurrency } from '../../../rsmCoreComponents/utils/formatters'
import { getCemFeatures } from '../../../store/userInfo/userInfoSlice'
import { isCemFeatureEnabled } from '../../../rsmCoreComponents/utils/featureFlagUtils'
import { CEM_FEATURE_PAYMENT_ACCOUNT_MANAGEMENT } from '../../../utils/constants/constants'

interface AutoPaySetupSuccessProps {
  autoPaySetup: AutoPaySetup
}

const styles: Styles = {
  detailContainer: (theme) => ({
    [theme.breakpoints.only('desktop')]: {
      paddingTop: '4rem',
      marginLeft: '6.5rem',
      marginRight: '6.5rem',
    },
    [theme.breakpoints.only('tablet')]: {
      paddingTop: '4rem',
      paddingLeft: '2rem',
      paddingRight: '2rem',
    },
    [theme.breakpoints.only('mobile')]: {
      paddingLeft: '1rem',
      paddingRight: '1rem',
    },
  }),
  detailSection: () => ({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'start',
    alignItems: 'start',
  }),
  details: () => ({
    flex: '1 1 auto',
  }),
  detailRow: () => ({
    flex: '1 1 auto',
    display: 'flex',
    justifyContent: 'start',
    paddingTop: '1.5rem',
    paddingBottom: '1.5rem',
  }),
  detailLabel: (theme) => ({
    fontWeight: 'bold',
    [theme.breakpoints.only('desktop')]: {
      minWidth: '15rem',
    },
    [theme.breakpoints.only('tablet')]: {
      minWidth: '10rem',
      fontSize: '0.875rem',
    },
    [theme.breakpoints.only('mobile')]: {
      minWidth: '9rem',
      fontSize: '0.875rem',
    },
  }),
  detailLabelContent: (theme) => ({
    [theme.breakpoints.only('tablet')]: {
      minWidth: '10rem',
      fontSize: '0.875rem',
    },
    [theme.breakpoints.only('mobile')]: {
      minWidth: '3rem',
      fontSize: '0.875rem',
      paddingLeft: '0.5rem',
    },
  }),
  autopay: () => ({
    flex: '0 0 auto',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '20rem',
    height: '10rem',
  }),
}

const AutoPaySetupSuccess = ({ autoPaySetup }: AutoPaySetupSuccessProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { isDesktop } = useDeviceType()
  const isEditAutoPay = useSelector(getIsEditAutoPay)
  const cemFeatures = useSelector(getCemFeatures)

  const isAccountManager = useMemo(
    () =>
      isCemFeatureEnabled(
        CEM_FEATURE_PAYMENT_ACCOUNT_MANAGEMENT,
        cemFeatures,
        'any',
      ),
    [cemFeatures],
  )

  const handleBackToButtonNavigation = () => {
    dispatch(setAutoPaySetup(null))
    if (isAccountManager) {
      navigate(`/invoicing/account-management/${autoPaySetup?.clientId}`)
    } else {
      navigate(`/invoicing/dashboard/${autoPaySetup?.clientId}`)
    }
  }
  const StyledLink = styled(Link)(({ theme }) => ({
    color: theme.palette.secondary.main,
    fontWeight: '800',
    textDecoration: 'underline',
  }))
  const autoPaySucessDescription = (
    <Box>
      <Trans
        i18nKey={
          isEditAutoPay
            ? 'Invoicing.AutoPaySetupSuccessPage.AutoPayEditDescription'
            : 'Invoicing.AutoPaySetupSuccessPage.AutoPaySetupDescription'
        }
        components={{
          PayHist: (
            <StyledLink
              to="/invoicing/payments"
              sx={{ textDecoration: 'none' }}
            />
          ),
        }}
      />
    </Box>
  )

  return (
    <>
      <InvoicesHeader
        primaryHeaderId="AutoPay"
        primaryHeaderText={
          isEditAutoPay
            ? t('Invoicing.AutoPaySetupSuccessPage.AutoPayEdit')
            : t('Invoicing.AutoPaySetupSuccessPage.AutoPaySetup')
        }
        primaryHeaderDescription={autoPaySucessDescription}
        backButtonId={
          isAccountManager ? 'BackToAccountManagement' : 'BackToDashboard'
        }
        backButtonText={
          isAccountManager
            ? t('Invoicing.AutoPaySetupSuccessPage.BackToAccountManagement')
            : t('Invoicing.AutoPaySetupSuccessPage.BackToDashboard')
        }
        handleNavigation={handleBackToButtonNavigation}
      />
      <Box sx={{ backgroundColor: tokens.colors.white }}>
        <Box sx={styles.detailContainer}>
          <Box sx={styles.detailSection}>
            <Box sx={styles.details}>
              <Box sx={styles.detailRow}>
                <Box sx={styles.detailLabel}>
                  {t('Invoicing.AutoPaySetupSuccessPage.Frequency')}
                </Box>
                <Box sx={styles.detailLabelContent}>
                  {autoPaySetup?.frequency}
                </Box>
              </Box>
              <hr aria-hidden="true" />
              <Box sx={styles.detailRow}>
                <Box sx={styles.detailLabel}>
                  {t('Invoicing.AutoPaySetupSuccessPage.AutoPayStartDate')}
                </Box>
                <Box sx={styles.detailLabelContent}>
                  {t(
                    'Invoicing.AutoPaySetupSuccessPage.AutoPayStartDateFormat',
                    {
                      date: autoPaySetup?.startDate,
                    },
                  )}
                </Box>
              </Box>
              {autoPaySetup?.autoPaylimit && (
                <>
                  <hr aria-hidden="true" />
                  <Box sx={styles.detailRow}>
                    <Box sx={styles.detailLabel}>
                      {t('Invoicing.AutoPaySetupSuccessPage.AutoPayLimit')}
                    </Box>
                    <Box sx={styles.detailLabelContent}>
                      {formatCurrency(autoPaySetup?.autoPaylimit || 0, 'USD')}
                    </Box>
                  </Box>
                </>
              )}
              <hr aria-hidden="true" />
              <Box sx={styles.detailRow}>
                <Box sx={styles.detailLabel}>
                  {t('Invoicing.AutoPaySetupSuccessPage.PaymentMethod')}
                </Box>
                <Box sx={styles.detailLabelContent}>
                  {getPaymentMethodDisplay(
                    autoPaySetup?.paymentMethod,
                    autoPaySetup?.accountNumber,
                  )}
                </Box>
              </Box>
              <hr aria-hidden="true" />
              <Box sx={styles.detailRow}>
                <Box sx={styles.detailLabel}>
                  <Box>{t('Invoicing.AutoPaySetupSuccessPage.PayerName')}</Box>
                </Box>
                <Box sx={styles.detailLabelContent}>
                  <Box>{autoPaySetup?.payer}</Box>
                </Box>
              </Box>
              <hr aria-hidden="true" />
              <Box sx={styles.detailRow}>
                <Box sx={styles.detailLabel}>
                  <Box>{t('Invoicing.AutoPaySetupSuccessPage.ClientName')}</Box>
                </Box>
                <Box sx={styles.detailLabelContent}>
                  <Box>{autoPaySetup?.companyName}</Box>
                </Box>
              </Box>
            </Box>
            {isDesktop && <Box sx={styles.autopay} />}
          </Box>
        </Box>
      </Box>
    </>
  )
}

export default AutoPaySetupSuccess
